export default class ChatSocket {
	constructor(url, opts = {}) {
		if(!url) throw new Error('WebSocket URL required');
		this.url = url;

		// options with sensible defaults
		this.pingIntervalMs = opts.pingIntervalMs ?? 20000;
		this.pongTimeoutMs = opts.pongTimeoutMs ?? 10000;
		this.backoffBaseMs = opts.backoffBaseMs ?? 500;
		this.backoffMaxMs = opts.backoffMaxMs ?? 30000;
		this.backoffFactor = opts.backoffFactor ?? 2;

		// internals
		this.ws = null;
		this._connecting = false;
		this._backoffAttempt = 0;
		this._backoffTimer = null;

		// subscriptions: { id: { hash, fns: { type: fn } } }
		this.subscriptions = {};
		this._currentHash = null;

		// ping/pong
		this._pingTimer = null;
		this._pongTimer = null;

		this._explicitlyClosed = false;
	}

	subscribe(hash, handlers) {
		if(!hash || typeof handlers !== 'object') {
			throw new Error('subscribe requires (hash, handlersObject)')
		}

		if(this._currentHash !== null && this._currentHash !== hash) {
			this.subscriptions = {};
			this._currentHash = null;
			if(this._backoffTimer) { clearTimeout(this._backoffTimer); this._backoffTimer = null; }
			this._closeSocket();
		}

		if(this._currentHash === null) {
			this._currentHash = hash;
		}

		const id = crypto.randomUUID?.() ?? (Math.random().toString(36) + Date.now().toString(36));
		this.subscriptions[id] = { hash, fns: {} };

		for(const type in handlers) {
			if(typeof handlers[type] !== 'function') {
				throw new Error(`Handler for type ${type} must be a function`);
			}
			this.subscriptions[id].fns[type] = handlers[type];
			if(this._isOpen()) {
				this._sendSubscribe(type);
			}
		}

		if(!this._isOpen()) {
			this._explicitlyClosed = false;
			this._connect();
		}

		return id;
	}

	unsubscribe(id) {
		if(!(id in this.subscriptions)) throw new Error(`Subscription ${id} not found`);
		const removed = this.subscriptions[id];
		delete this.subscriptions[id];

		// send unsubscribe for all types associated with this id, if no other subscription has them
		for(const type in removed.fns) {
			let stillExists = false;
			for(const otherId in this.subscriptions) {
				if(type in this.subscriptions[otherId].fns) {
					stillExists = true;
					break;
				}
			}
			if(!stillExists && this._isOpen()) {
				this._sendUnsubscribe(removed.hash, type);
			}
		}

		if(Object.keys(this.subscriptions).length === 0) {
			this._currentHash = null
			this._explicitlyClosed = true
			this._closeSocket('no-subscriptions')
		}
	}

	_isOpen() {
		return this.ws?.readyState === WebSocket.OPEN
	}

	_connect() {
		if(this._isOpen() || this._connecting) return
		this._connecting = true

		try {
			this.ws = new WebSocket(`${this.url}?hash=${this._currentHash}`)
		} catch (err) {
			this._connecting = false
			this._scheduleReconnect()
			return
		}

		this.ws.onopen = () => {
			this._connecting = false
			this._backoffAttempt = 0
			this._onOpen()
		};

		this.ws.onmessage = (ev) => this._onMessage(ev)
		this.ws.onclose = (ev) => this._onClose(ev)
		this.ws.onerror = (err) => this._onError(err)
	}

	_onOpen() {
		const types = new Set()
		for (const id in this.subscriptions) {
			for(const type in this.subscriptions[id].fns) {
				types.add(type);
			}
		}
		for (const type of types) this._sendSubscribe(type)
		this._startPingLoop()
	}

	_onMessage(ev) {
		let data = ev.data
		try {
			if(typeof data !== 'string') data = data.toString()
		} catch (e) {
			console.error('Converting message to string', e);
		}

		let parsed = null
		try {
			parsed = JSON.parse(data);
		} catch (e) {
			console.error(e);
			return;
		}

		if(parsed?.type === 'pong') {
			this._handlePong()
			return
		}

		if(parsed?.type) {
			this._dispatchEvent(parsed.type, parsed.data);
		}
	}

	_onClose() {
		this._clearPingLoop()
		this.ws.onopen = this.ws.onclose = this.ws.onmessage = this.ws.onerror = null;
		this.ws = null
		this._connecting = false
		if(!this._explicitlyClosed) this._scheduleReconnect()
	}

	_onError(err) { console.error(err); }

	_scheduleReconnect() {
		if(Object.keys(this.subscriptions).length === 0) return

		this._backoffAttempt++
		let delay = Math.min(this.backoffMaxMs, Math.round(this.backoffBaseMs * Math.pow(this.backoffFactor, this._backoffAttempt - 1)))
		delay = Math.round(delay * (0.5 + Math.random() * 0.5))

		if(this._backoffTimer) clearTimeout(this._backoffTimer)
		this._backoffTimer = setTimeout(() => {
			this._backoffTimer = null
			this._connect()
		}, delay)
	}

	_closeSocket() {
		if(!this.ws) return
		this.ws.onopen = this.ws.onclose = this.ws.onmessage = this.ws.onerror = null;
		try { this.ws.close() } catch (e) { console.error(e); }
		this._clearPingLoop()
		this.ws = null
		this._connecting = false
	}

	_sendRaw(obj) {
		if(!this._isOpen()) return
		try { this.ws.send(JSON.stringify(obj)) } catch (e) { console.error(e); }
	}

	_sendSubscribe(type) {
		this._sendRaw({ action: 'subscribe', hash: this._currentHash, type })
	}

	_sendUnsubscribe(hash, type) {
		this._sendRaw({ action: 'unsubscribe', hash, type });
	}

	_dispatchEvent(type, payload) {
		for (const id in this.subscriptions) {
			const fns = this.subscriptions[id].fns;
			if(type in fns) {
				setTimeout(() => {
					try { fns[type](payload, type) } catch (e) { console.error('Dispatching', type, e) }
				}, 0)
			}
		}
	}

	_startPingLoop() {
		this._clearPingLoop()
		this._pingTimer = setInterval(() => this._sendPing(), this.pingIntervalMs)
		this._sendPing()
	}

	_clearPingLoop() {
		if(this._pingTimer) { clearInterval(this._pingTimer); this._pingTimer = null }
		if(this._pongTimer) { clearTimeout(this._pongTimer); this._pongTimer = null }
	}

	_sendPing() {
		if(!this._isOpen()) return
		if(this._pongTimer) clearTimeout(this._pongTimer)
		this._pongTimer = setTimeout(() => {
			try { if(this.ws) this.ws.close() } catch (e) { console.error(e); }
			this._clearPingLoop()
		}, this.pongTimeoutMs)

		this._sendRaw({ type: 'ping', ts: Date.now() })
	}

	_handlePong() {
		if(this._pongTimer) { clearTimeout(this._pongTimer); this._pongTimer = null }
	}
}
